import {
  documentFromGrid,
  SpreadsheetDocument,
  SpreadsheetGridInput,
} from 'models/SpreadsheetDocument';
import { readWorkbookSheets } from './readWorkbookSheets';
import { extractSheetAsGrid, parseExcelWorkbook } from 'pages/SiteSettingsPage/excelImport';

export interface SpreadsheetFolderImportResult {
  documents: SpreadsheetDocument[];
  issues: { path: string; message: string }[];
  ignoredFiles: number;
}

interface ImportSpreadsheetFolderOptions {
  allSheets?: boolean;
  importGrid?: (grid: SpreadsheetGridInput) => SpreadsheetGridInput | string;
  selectSheet: (path: string, sheetNames: string[]) => Promise<string | null>;
  onProgress: (completed: number, total: number) => void;
  signal: AbortSignal;
}

/** 하위 폴더의 엑셀도 파일마다 문서 하나로 읽고, 실패한 파일은 경로와 사유를 반환한다. */
export async function importSpreadsheetFolder(
  files: File[],
  {
    importGrid,
    selectSheet,
    onProgress,
    signal,
    allSheets = false,
  }: ImportSpreadsheetFolderOptions,
): Promise<SpreadsheetFolderImportResult> {
  const excelFiles = files
    .filter((file) => /\.(xlsx|xls)$/i.test(file.name) && !/^(~\$|\._)/.test(file.name))
    .sort((a, b) =>
      (a.webkitRelativePath || a.name).localeCompare(b.webkitRelativePath || b.name, 'ko', {
        numeric: true,
      }),
    );
  const result: SpreadsheetFolderImportResult = {
    documents: [],
    issues: [],
    ignoredFiles: files.length - excelFiles.length,
  };
  onProgress(0, excelFiles.length);

  // 한 파일씩 읽어 다수의 워크북을 동시에 메모리에 보관하지 않는다.
  for (const [index, file] of excelFiles.entries()) {
    if (signal.aborted) break;
    const path = file.webkitRelativePath || file.name;
    try {
      const { workbook, sheetNames } = await parseExcelWorkbook(file);
      if (signal.aborted) break;
      if (sheetNames.length === 0) throw new Error('시트가 없는 파일입니다.');
      const relativeName = path.split('/').slice(1).join('/') || file.name;
      const base = relativeName.replace(/\.[^.]+$/, '') || relativeName;
      if (allSheets) {
        const { sheets, issues } = readWorkbookSheets(workbook, sheetNames, importGrid);
        result.documents.push(
          ...sheets.map(({ sheetName, grid }) =>
            documentFromGrid(sheetNames.length > 1 ? `${base} (${sheetName})` : base, grid),
          ),
        );
        result.issues.push(
          ...issues.map((issue) => ({
            path: `${path} (${issue.sheetName})`,
            message: issue.message,
          })),
        );
        onProgress(index + 1, excelFiles.length);
        continue;
      }
      const sheetName =
        sheetNames.length === 1 ? sheetNames[0] : await selectSheet(path, sheetNames);
      if (signal.aborted) break;
      if (!sheetName) throw new Error('시트를 선택하지 않아 건너뛰었습니다.');

      const rawGrid = extractSheetAsGrid(workbook, sheetName);
      const grid = importGrid ? importGrid(rawGrid) : rawGrid;
      if (typeof grid === 'string') throw new Error(grid);
      if (grid.headers.length === 0 || grid.dataRows.length === 0) {
        throw new Error('가져올 데이터가 없습니다(빈 시트).');
      }

      const name = sheetNames.length > 1 ? `${base} (${sheetName})` : base;
      result.documents.push(documentFromGrid(name, grid));
    } catch (error) {
      if (signal.aborted) break;
      result.issues.push({
        path,
        message: error instanceof Error ? error.message : '파일 읽기 실패',
      });
    }
    onProgress(index + 1, excelFiles.length);
  }
  return result;
}

/** 기존 문서를 유지하며 새 문서를 추가하고, 제목이 겹치면 번호로 구분한다. */
export function appendImportedDocuments(
  existing: SpreadsheetDocument[],
  imported: SpreadsheetDocument[],
): SpreadsheetDocument[] {
  const names = new Set(existing.map((doc) => doc.name));
  return [
    ...existing,
    ...imported.map((doc) => {
      let name = doc.name;
      let suffix = 2;
      while (names.has(name)) name = `${doc.name} (${suffix++})`;
      names.add(name);
      return name === doc.name ? doc : { ...doc, name };
    }),
  ];
}
